import groupBy from "lodash.groupby";
import sortBy from "lodash.sortby";
import startCase from "lodash.startcase";
import React from "react";
import { Updater } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import { ConfigurationDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { usePatchConfigurations } from "../../../../../api/configurations/patch-configurations";
import { useGetOrg } from "../../../../../api/organizations/get-organization";
import {
  getNewModifiedConfigs,
  onConfigChange,
  onLockedChange,
} from "../../../Utils/Utils";

import { ConfigurationInput } from "./index";

interface AdvancedConfigurationsProps {
  settingsState: ConfigurationDto[];
  setSettingsState: Updater<ConfigurationDto[]>;
}

const titles = {
  GENERAL: "Shopify Configurations",
  PLUGIN_CONFIG: "Plugin Configurations",
  WEB_UI_SCOPE: "Web App Configurations",
  UI_CONFIG: "UI Configurations",
  ANALYTICS: "Analytics Configurations",
};

const subtitles = {
  GENERAL:
    "This information is captured during Intelligems installation but requires manual updates if changed within Shopify later.",
};

//todo db driven
const tooltipMessages = {
  shopify_plan:
    "Shopify Plan at time of install. Controls several defaults for settings, including which version of Cart Transform Functions account will use for Price Testing.",
  shop_weight_unit: "Weight unit used for tiered rate shipping tests.",
  locale: "Used to display correct units throughout Intelligems app",
  shop_currency: "Currency used in price tests and analytics.",
};

const titleMapping = {
  shop_weight_unit: "Shopify Weight Unit",
  locale: "Shopify Locale",
  shop_currency: "Shopify Default Currency",
  shopify_functions_enabled: "Shopify Functions For Offers Enabled",
};

const topicOverrides = {
  shop_weight_unit: "GENERAL",
  locale: "GENERAL",
  shop_currency: "GENERAL",
  shopify_functions_enabled: "PLUGIN_CONFIG",
  shopify_functions_for_pricing_enabled: "PLUGIN_CONFIG",
  price_splitting_enabled: "PLUGIN_CONFIG",
};

const keysToExclude = new Set([
  "auto_sync_cogs",
  "shipping_cost_per_order",
  "transaction_fee_pct",
  "should_pull_cost_from_shopify",
  "cart_transform_mode",
  "css",
  "javascript",
  "domain",
  "use_beacon",
  "use_browser_locale",
  "should_hide_shipping_subtext",
  "shipping_progress_bar_config",
  "volume_discount_progress_bar_widget",
  "volume_discount_cart_message_widget",
  "volume_discount_quantity_widget",
  "ga_cd_index",
  "onboarding_test_flow",
  "enable_ai",
  "enable_price_csv_download",
  "should_show_profit_in_dashboard",
  "should_show_sitewide_dashboard",
  "edit_currency_filter_for_price_tests",
  "enable_schedule_tests",
  "live_chat_enabled",
  "max_enabled_offers",
  "enable_bundle_products",
  "functions_include_subscription_products",
]);

const shouldIncludeAutoGenConfig = (config: ConfigurationDto) => {
  if (config.topic === "ANALYTICS") return false;
  if (keysToExclude.has(config.key)) return false;
  return true;
};

export const AdvancedConfigurations = ({
  settingsState,
  setSettingsState,
}: AdvancedConfigurationsProps) => {
  const mutateConfigurations = usePatchConfigurations();
  const organization = useGetOrg();

  return (
    <Grid container direction={"column"} rowSpacing={2}>
      {sortBy(
        Object.entries(
          groupBy(
            settingsState.filter(shouldIncludeAutoGenConfig),
            (_) => topicOverrides[_.key] ?? _.topic
          )
        ),
        (_) => _[0]
      ).map(([key, settings], index) => {
        return (
          <Grid item key={`${key}-${index}`}>
            <IgPaper key={key}>
              <Grid container direction={"column"} rowSpacing={4}>
                <Grid item>
                  <IgTypography variant={"h3"} fontWeight={"fontWeightMedium"}>
                    {titles[key]}
                  </IgTypography>
                </Grid>
                {subtitles[key] && (
                  <Grid item mb={4}>
                    <IgTypography variant={"subtitle1"}>
                      {subtitles[key]}
                    </IgTypography>
                  </Grid>
                )}
                {Object.entries(groupBy(settings, (_) => _.type)).map(
                  ([key, settings], groupIndex) => {
                    return (
                      <Grid
                        container
                        direction={"column"}
                        rowSpacing={2}
                        key={`${key}-${groupIndex}`}
                      >
                        <Grid item>
                          <Grid
                            container
                            padding={4}
                            columnSpacing={4}
                            rowSpacing={4}
                          >
                            {sortBy(settings, "key").map((setting) => {
                              return (
                                <Grid
                                  item
                                  key={setting.key}
                                  xs={12}
                                  container
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <ConfigurationInput
                                    config={setting}
                                    onLockedChange={
                                      setting.topic === "WEB_UI_SCOPE"
                                        ? (locked) => {
                                            onLockedChange(
                                              setting.key,
                                              locked,
                                              setSettingsState
                                            );
                                          }
                                        : undefined
                                    }
                                    tooltipMessage={
                                      setting.key in tooltipMessages
                                        ? tooltipMessages[
                                            setting.key as keyof typeof tooltipMessages
                                          ]
                                        : undefined
                                    }
                                    title={
                                      setting.key in titleMapping
                                        ? titleMapping[
                                            setting.key as keyof typeof titleMapping
                                          ]
                                        : undefined
                                    }
                                    onChange={(newValue: any) =>
                                      onConfigChange(
                                        setting.key,
                                        newValue,
                                        setSettingsState
                                      )
                                    }
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                        <Grid item>
                          <IgDivider spacing={"m"} />
                        </Grid>
                      </Grid>
                    );
                  }
                )}
                <Grid item xs textAlign={"end"}>
                  <IgButton
                    loading={mutateConfigurations.isPending}
                    style={{ marginTop: 5 }}
                    size="medium"
                    variant={"contained"}
                    onClick={() =>
                      mutateConfigurations.mutate(
                        getNewModifiedConfigs(
                          settingsState,
                          organization.data.org.configurations
                        ) || []
                      )
                    }
                  >
                    Save Configurations
                  </IgButton>
                </Grid>
              </Grid>
            </IgPaper>
          </Grid>
        );
      })}
    </Grid>
  );
};
